import {
  type RefObject,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { type DateTimeInputRef } from '../../date-time-range-picker/types.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';

/*
 * Handles the focus event within 'DateTimeInput' components.
 * Moving the focus to the `DateTimeInput` focuses the 'to input' or the first empty spin button.
 * If everything is empty focus the 'from input'.
 * If the target element is one of the spin buttons or expression, the focus is not redirected.
 * @internal
 */
export const focusDateTimeInput = (
  event: ReactFocusEvent | ReactMouseEvent | null,
  refFrom: RefObject<DateTimeInputRef | null>,
  refTo?: RefObject<DateTimeInputRef | null>,
) => {
  const spinButtonHandlersTo = refTo?.current?.spinButtonHandlers;
  const spinButtonHandlersFrom = refFrom?.current?.spinButtonHandlers;

  /** Check if the target element is one of the spin buttons or expression. */
  const isTargetValid = (target: EventTarget | undefined) => {
    const spinButtonsFrom = Array.from(
      spinButtonHandlersTo?.values() || [],
    ).map((handler) => handler?.element.current);
    const spinButtonsTo = Array.from(
      spinButtonHandlersFrom?.values() || [],
    ).map((handler) => handler?.element.current);

    const allSpinButtons = [
      ...spinButtonsFrom,
      ...spinButtonsTo,
      ...[refTo?.current?.expressionInputRef],
      ...[refFrom?.current?.expressionInputRef],
    ];

    return (
      target !== undefined && allSpinButtons.includes(target as HTMLSpanElement)
    );
  };

  /**
   * Checks if any spin button in the provided handlers is empty (has no textContent).
   * Returns false if all spin buttons are empty or none are empty.
   */
  const isAnySpinButtonEmpty = (
    handlers:
      | Map<Intl.DateTimeFormatPartTypes, SpinButtonHandler | null>
      | undefined,
  ): boolean => {
    if (!handlers) {
      return false;
    }

    let hasEmpty = false;
    let allEmpty = true;

    for (const [, handler] of handlers) {
      const textContent = handler?.element.current?.textContent;
      if (textContent === '') {
        hasEmpty = true;
      } else {
        allEmpty = false;
      }
    }

    return hasEmpty && !allEmpty;
  };

  /**
   * Find the first spin button or the first empty one.
   */
  const findLastEntryWithTextContent = (
    handlers:
      | Map<Intl.DateTimeFormatPartTypes, SpinButtonHandler | null>
      | undefined,
  ) => {
    if (!handlers) {
      return null;
    }

    let firstSpinButtonHandler: SpinButtonHandler | null = null;

    for (const [, handler] of handlers) {
      if (!firstSpinButtonHandler) {
        firstSpinButtonHandler = handler;
      }
      if (!handler?.element.current?.textContent) {
        firstSpinButtonHandler = handler;
        break;
      }
    }

    return firstSpinButtonHandler;
  };

  if (isTargetValid(event?.target)) {
    return;
  }

  const emptyToSpinButton = isAnySpinButtonEmpty(spinButtonHandlersTo);
  const emptyFromSpinButton = isAnySpinButtonEmpty(spinButtonHandlersFrom);

  const firstFromSpinButtonHandler = findLastEntryWithTextContent(
    spinButtonHandlersFrom,
  );
  const firstToSpinButtonHandler =
    findLastEntryWithTextContent(spinButtonHandlersTo);

  // Focus the 'to spin buttons', when they have at least one empty.
  if (emptyToSpinButton && !emptyFromSpinButton) {
    const firstToSpinButtonHandler =
      findLastEntryWithTextContent(spinButtonHandlersTo);
    if (firstToSpinButtonHandler) {
      firstToSpinButtonHandler?.element.current?.focus();
      return;
    }
  }

  // Focus the 'from spin buttons' when they have at least one empty or when 'to input' is filled.
  if (
    emptyFromSpinButton ||
    (firstFromSpinButtonHandler?.element.current?.textContent !== '' &&
      (refTo?.current?.expressionInputRef?.textContent !== '' ||
        firstToSpinButtonHandler?.element.current?.textContent !== ''))
  ) {
    if (firstFromSpinButtonHandler) {
      firstFromSpinButtonHandler?.element.current?.focus();
      return;
    }
  }

  // Focus the 'to expression input' when 'from expression input' is filled and 'to input is empty'.
  if (
    refFrom?.current?.expressionInputRef?.textContent !== '' &&
    refTo?.current?.expressionInputRef?.textContent === '' &&
    firstToSpinButtonHandler?.element.current?.textContent === ''
  ) {
    refTo?.current?.expressionInputRef?.focus?.();
    return;
  }

  // Focus the 'from expression input'.
  refFrom?.current?.expressionInputRef?.focus();
};

/**
 * Attempts to move the focus to the "hour" spin button of the "from" input.
 * The hour spin button is only foucsable if the value is an absolute timeframe (ISO 8601)
 * and not an expression. If focus is not possible, falls back to {@link focusDateTimeInput}.
 * @param dateTimeFromRef - the ref of the "from" DateTimeInput component
 * @internal
 */
export const focusTimeInput = (
  dateTimeFromRef: RefObject<DateTimeInputRef | null>,
): void => {
  const hourElement =
    dateTimeFromRef.current?.spinButtonHandlers.get('hour')?.element.current;
  if (hourElement) {
    hourElement.focus();
    return;
  }
  focusDateTimeInput(null, dateTimeFromRef);
};

const MAX_FOCUS_ATTEMPTS = 3;

/**
 * Repeatedly attempts to focus an element using requestAnimationFrame until
 * focus succeeds or max attempts are reached. This handles race conditions
 * where popover close animations restore focus to the trigger element.
 * @param getElement - getter that returns the element to focus
 * @param dateTimeRef - optional ref to fall back to via {@link focusDateTimeInput} if all attempts fail
 * @internal
 */
export const focusElementWithRetry = (
  getElement: () => HTMLElement | null | undefined,
  dateTimeRef?: RefObject<DateTimeInputRef | null>,
): void => {
  let attempts = 0;

  const tryFocus = (): void => {
    attempts++;
    const element = getElement();

    if (element) {
      element.focus();
      if (document.activeElement === element) {
        return;
      }
    }

    if (attempts < MAX_FOCUS_ATTEMPTS) {
      requestAnimationFrame(tryFocus);
      return;
    }

    if (dateTimeRef) {
      focusDateTimeInput(null, dateTimeRef);
    }
  };

  requestAnimationFrame(tryFocus);
};
